import React, { useMemo, useState } from 'react';
import { Ban, Bot, Copy, Pencil, Plus, Power, RefreshCw, Route, Search, ShieldAlert } from 'lucide-react';
import { CLIENT_TYPE_OPTIONS, MCP_PUBLIC_URL, clientTypeLabel } from '../governedMcpConstants';
import { governedMcpApi } from '../governedMcpApi';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import type { McpClient } from '../governedMcpTypes';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import { fieldClass, ghostBtn, primaryBtn } from '../components/GatewayUi';
import { ClientStatusBadge, EnvBadge } from '../components/StatusBadges';
import ClientFormDrawer from '../drawers/ClientFormDrawer';

const TYPE_ICON = Bot;

export default function ClientsTab({
  isLight,
  onToast,
}: {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, saveClient, setClientStatus, deleteClient } = useGovernedMcp();
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<McpClient | null | 'new'>(null);
  const [confirm, setConfirm] = useState<{ client: McpClient; action: 'revoke' | 'delete' | 'rotate' } | null>(null);
  const [rotatingId, setRotatingId] = useState<string | null>(null);
  const occupiedRegistrationIds = useMemo(() => {
    const currentId = editing && editing !== 'new' ? editing.id : null;
    return state.clients
      .filter((client) => client.id !== currentId)
      .map((client) => client.registrationId)
      .filter(Boolean);
  }, [state.clients, editing]);

  const copyRouteToken = async (client: McpClient) => {
    const token = client.llmRouteToken?.trim() || '';
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      onToast('success', 'Route token copied');
      logGovernedMcp('app', 'client_card_llm_route_copied', { id: client.id });
    } catch {
      onToast('error', 'Could not copy to clipboard');
      logGovernedMcp('warning', 'client_card_llm_route_copy_failed', { id: client.id });
    }
  };

  const rotateRouteToken = async (client: McpClient) => {
    const gatewayId = client.llmGatewayId?.trim() || '';
    if (!gatewayId) {
      onToast('error', 'This client has no LLM gateway selected.');
      logGovernedMcp('warning', 'client_card_llm_route_rotate_blocked', { id: client.id, reason: 'missing_gateway' });
      return;
    }
    setRotatingId(client.id);
    logGovernedMcp('app', 'client_card_llm_route_rotate', { id: client.id, llmGatewayId: gatewayId });
    try {
      const issued = await governedMcpApi.issueLlmRouteToken({
        clientId: client.id,
        llmGatewayId: gatewayId,
        registrationId: client.registrationId,
        persist: true,
      });
      saveClient({
        ...client,
        llmRouteEnabled: true,
        llmGatewayId: gatewayId,
        llmRouteToken: issued.token,
        llmRouteExpiresAt: issued.expiresAt,
      });
      onToast('success', `Route token rotated for “${client.name}”.`);
      logGovernedMcp('app', 'client_card_llm_route_rotate_ok', { id: client.id, expiresAt: issued.expiresAt });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to rotate the route token.';
      onToast('error', message);
      logGovernedMcp('error', 'client_card_llm_route_rotate_failed', { id: client.id, message });
    } finally {
      setRotatingId(null);
    }
  };

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return state.clients.filter((client) => {
      if (!q) return true;
      return `${client.name} ${client.registrationId} ${clientTypeLabel(client.clientType)}`.toLowerCase().includes(q);
    });
  }, [state.clients, query]);

  return (
    <div>
      <div
        className={`rounded-lg border px-4 py-3 mb-4 text-sm ${
          isLight ? 'border-indigo-200 bg-indigo-50 text-indigo-900' : 'border-indigo-500/30 bg-indigo-500/10 text-indigo-100'
        }`}
      >
        <ShieldAlert size={16} className="inline mr-2 -mt-0.5" />
        Selfic never trusts a user identity asserted by Claude, ChatGPT, Copilot, or any other client. The IdP token is mapped
        to the enterprise user, then existing Selfic permissions apply.
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
          <input className={`${fieldClass(isLight)} pl-9`} placeholder="Search clients…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <button type="button" className={primaryBtn()} onClick={() => setEditing('new')}>
          <Plus size={14} /> Register client
        </button>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          isLight={isLight}
          title="No registered AI clients"
          description="Register Claude Enterprise, ChatGPT, Copilot, or an internal agent against the MCP host."
          action={
            <button type="button" className={primaryBtn()} onClick={() => setEditing('new')}>
              Register client
            </button>
          }
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((client) => (
            <article
              key={client.id}
              className={`rounded-xl border p-4 flex flex-col ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-2 rounded-lg ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
                    <TYPE_ICON size={18} className="text-indigo-400" />
                  </div>
                  <div className="min-w-0">
                    <h3 className={`font-semibold truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{client.name}</h3>
                    <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                      {clientTypeLabel(client.clientType)} · {client.registrationId}
                    </p>
                  </div>
                </div>
                <ClientStatusBadge status={client.status} />
              </div>
              <dl className={`grid grid-cols-2 gap-2 text-xs mt-4 ${isLight ? 'text-neutral-600' : 'text-neutral-400'}`}>
                <div>
                  <dt>MCP URL</dt>
                  <dd className={`font-mono break-all ${isLight ? 'text-neutral-900' : 'text-white'}`}>{MCP_PUBLIC_URL}</dd>
                </div>
                <div>
                  <dt>Environment</dt>
                  <dd>
                    <EnvBadge environment={client.environment} />
                  </dd>
                </div>
                <div>
                  <dt>Trust</dt>
                  <dd className="capitalize">{client.trustPolicy.replace(/_/g, ' ')}</dd>
                </div>
                <div>
                  <dt>Last connected</dt>
                  <dd>{client.lastConnectedAt ? new Date(client.lastConnectedAt).toLocaleString() : 'Never'}</dd>
                </div>
                <div className="col-span-2">
                  <dt>Auth</dt>
                  <dd>{client.authConfig}</dd>
                </div>
                {client.clientType === 'custom' && client.llmRouteEnabled ? (
                  <div className="col-span-2">
                    <dt>LLM route</dt>
                    <dd className="flex items-center gap-1.5">
                      <Route size={12} className="text-indigo-500" />
                      {client.llmRouteExpiresAt
                        ? `Token expires ${new Date(client.llmRouteExpiresAt).toLocaleString()}`
                        : 'Route token issued'}
                    </dd>
                  </div>
                ) : null}
                <div>
                  <dt>Created by</dt>
                  <dd>{client.createdBy}</dd>
                </div>
              </dl>
              <div className="flex flex-wrap gap-2 mt-4">
                <button type="button" className={ghostBtn(isLight)} onClick={() => setEditing(client)}>
                  <Pencil size={14} /> Edit
                </button>
                {client.clientType === 'custom' && client.llmRouteToken ? (
                  <>
                    <button type="button" className={ghostBtn(isLight)} onClick={() => void copyRouteToken(client)}>
                      <Copy size={14} /> Copy token
                    </button>
                    <button
                      type="button"
                      className={ghostBtn(isLight)}
                      disabled={rotatingId === client.id}
                      onClick={() => setConfirm({ client, action: 'rotate' })}
                    >
                      <RefreshCw size={14} className={rotatingId === client.id ? 'animate-spin' : ''} /> Rotate token
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className={ghostBtn(isLight)}
                  onClick={() => {
                    const next = client.status === 'active' ? 'disabled' : 'active';
                    setClientStatus(client.id, next);
                    onToast('success', next === 'active' ? 'Client enabled' : 'Client disabled');
                  }}
                >
                  <Power size={14} /> {client.status === 'active' ? 'Disable' : 'Enable'}
                </button>
                <button type="button" className={ghostBtn(isLight) + ' text-red-400'} onClick={() => setConfirm({ client, action: 'revoke' })}>
                  <Ban size={14} /> Revoke
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <p className={`text-xs mt-4 ${isLight ? 'text-neutral-500' : 'text-neutral-500'}`}>
        Supported examples: {CLIENT_TYPE_OPTIONS.map((option) => option.label).join(' · ')}
      </p>

      <ClientFormDrawer
        open={editing !== null}
        initial={editing && editing !== 'new' ? editing : null}
        occupiedRegistrationIds={occupiedRegistrationIds}
        isLight={isLight}
        onClose={() => setEditing(null)}
        onToast={onToast}
        onPersistClient={saveClient}
        onSave={(client) => {
          saveClient(client);
          setEditing(null);
          onToast('success', `Client “${client.name}” is available when creating policies.`);
        }}
      />
      <ConfirmDialog
        open={Boolean(confirm)}
        title={
          confirm?.action === 'revoke'
            ? 'Revoke client immediately?'
            : confirm?.action === 'rotate'
              ? 'Rotate the route token?'
              : 'Delete client?'
        }
        description={
          confirm?.action === 'revoke'
            ? `${confirm.client.name} will be unable to discover or invoke any capability.`
            : confirm?.action === 'rotate'
              ? `The current JWT for ${confirm.client.name} stops working as soon as the new token is issued.`
              : 'This removes the registration from the gateway.'
        }
        confirmLabel={confirm?.action === 'revoke' ? 'Revoke now' : confirm?.action === 'rotate' ? 'Rotate token' : 'Delete'}
        danger
        isLight={isLight}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;
          const next = confirm;
          setConfirm(null);
          if (next.action === 'revoke') {
            setClientStatus(next.client.id, 'revoked');
            onToast('success', 'Client revoked');
            return;
          }
          if (next.action === 'rotate') {
            void rotateRouteToken(next.client);
            return;
          }
          deleteClient(next.client.id);
          onToast('success', 'Client deleted');
        }}
      />
    </div>
  );
}
